import gettext from '@sdk/lang/gettext';

import config from './config';
import isCdnResizeEnabled from './components/services/isCdnResizeEnabled';
import { LABOR_COST_CALCULATOR_TYPE } from './constants';

export default function configureCalendarSdk(Authentication, PersonaCollection, DayPartsService, sdk, $injector) {
  'ngInject';

  const canSeeUserShifts = sdk('process/canSeeUserShifts');
  const WebSocketService = sdk('WebSocketService');
  const calendarContext = sdk('context');

  Object.defineProperties(calendarContext, {
    usersCanSeeSchedulesInOtherLocations: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.usersCanSeeSchedulesInOtherLocations;
      },
    },
    usersCanSeeSchedulesForOtherPositions: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.usersCanSeeSchedulesForOtherPositions;
      },
    },
    usersCanSeeOtherSchedules: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.usersCanSeeOtherSchedules;
      },
    },
    managersCanSeeSchedulesInOtherLocations: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.managersCanSeeSchedulesInOtherLocations;
      },
    },
    usTimeFormat: {
      configurable: true,
      get() {
        return Authentication.user?.settings.usTimeFormat;
      },
    },
    shiftAppearance: {
      configurable: true,
      get() {
        return Authentication.user?.settings.shiftAppearance || {};
      },
    },
    showLastNameFirst: {
      configurable: true,
      get() {
        return Authentication.user?.settings.showLastNameFirst;
      },
    },
    firstDayOfWeek: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.firstDayOfWeek;
      },
    },
    firstDayOfMonth: {
      configurable: true,
      get() {
        return Authentication.user?.orgSettings.firstDayOfMonth;
      },
    },
    laborCostSettings: {
      configurable: true,
      get() {
        return (
          Authentication.user && {
            isIcelandicLaborCost:
              Authentication.user.orgSettings.laborCostCalculatorType ===
              LABOR_COST_CALCULATOR_TYPE.ICELAND,
            overtimeEnabled: Authentication.user.orgSettings.overtimeEnabled,
            overtimeWeekThreshold: Authentication.user.orgSettings.overtimeWeekThreshold,
            overtimeWeekMultiplier: Authentication.user.orgSettings.overtimeWeekMultiplier,
            spreadOfHoursEnabled: Authentication.user.orgSettings.spreadOfHoursEnabled,
            spreadOfHoursThreshold: Authentication.user.orgSettings.spreadOfHoursThreshold,
            spreadOfHoursPay: Authentication.user.orgSettings.spreadOfHoursPay,
            holidaysEnabled: Authentication.user.orgSettings.holidaysEnabled,
            holidayRateMultiplier: Authentication.user.orgSettings.holidayRateMultiplier,
            shiftBreakType: Authentication.user.orgSettings.shiftBreakType,
            shiftSplitTime: Authentication.user.orgSettings.shiftSplitTime,
          }
        );
      },
    },
    me: {
      configurable: true,
      get() {
        return PersonaCollection.me;
      },
    },
    orgId: {
      configurable: true,
      get() {
        return Authentication.user.org.id;
      },
    },
    orgUsers: {
      configurable: true,
      get() {
        return PersonaCollection.users;
      },
    },
    orgAllUsers: {
      configurable: true,
      get() {
        return PersonaCollection.allUsers;
      },
    },
    orgLocations: {
      configurable: true,
      get() {
        return PersonaCollection.locations;
      },
    },
    orgPositions: {
      configurable: true,
      get() {
        return PersonaCollection.positions;
      },
    },
    orgGroups: {
      configurable: true,
      get() {
        return PersonaCollection.groups;
      },
    },
    orgSettings: {
      configurable: true,
      get() {
        return Authentication.user.orgSettings;
      },
    },
    personasDict: {
      configurable: true,
      get() {
        return PersonaCollection.personasDict;
      },
    },
    authorizationToken: {
      configurable: true,
      get() {
        return Authentication.token;
      },
    },
    personasHiddenOnScheduleDict: {
      configurable: true,
      get() {
        return Authentication.user?.personasHiddenOnScheduleDict || {};
      },
    },
    isPrefab: {
      configurable: true,
      get() {
        return Authentication.isPrefab;
      },
    },
    viewDatesParam: {
      configurable: true,
      get() {
        return $injector.get('ShiftUtils').viewDatesParam;
      },
    },
    laborUISchema: {
      configurable: true,
      get() {
        return $injector.get('LaborUISchema').schema?.calendarUISchema;
      },
    },
    isPrefabTimeoffDisabled: {
      configurable: true,
      get() {
        return false; // not sure how mobiles work? Maybe we can get rid of it from sdk as well?
      },
    },
    isWorldClock: {
      configurable: true,
      get() {
        return Authentication.user.settings.worldClockEnabled;
      },
    },
    isOrgFeatureEnabled: {
      configurable: true,
      get() {
        return Authentication.isOrgFeatureEnabled;
      },
    },
    tags: {
      configurable: true,
      get() {
        return PersonaCollection.tags;
      },
    },
    shouldProcessServerLaborData: {
      configurable: true,
      get() {
        return Authentication.isOrgFeatureEnabled('laborCost');
      },
    },
    unavailabilityApproval: {
      configurable: true,
      get() {
        return $injector.get('AvailabilityUtils').isApprovalEnabled();
      },
    },
    shiftAcceptanceEnabled: {
      configurable: true,
      get() {
        return (
          config.SHIFT_ACCEPTANCE_ENABLED && Authentication.user.orgSettings.shiftAcceptanceEnabled
        );
      },
    },
    consecutiveWorkDaysLimitEnabled: {
      configurable: true,
      get() {
        return config.CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED;
      },
    },
    gettext: {
      configurable: true,
      get() {
        return gettext;
      },
    },
    dayParts: {
      configurable: true,
      get() {
        return DayPartsService.dayParts;
      },
    },
    serverSideFilteringEnabled: {
      configurable: true,
      get() {
        return config.SERVER_SIDE_FILTERING_ENABLED;
      },
    },
    // SLING-1021: the getter is lazily evaluated per image render, so the
    // headless boot path (.run()) never touches LD — isCdnResizeEnabled()
    // compiles to the config constant in the headless bundle (RF-35411).
    isCdnResizeEnabled: {
      configurable: true,
      get() {
        return isCdnResizeEnabled();
      },
    },
  });

  Object.assign(calendarContext, {
    getUserGroups: (user) => PersonaCollection.getRelationships(user.id),
    canSeeUser: (user) => !user.id || canSeeUserShifts(user),
  });

  calendarContext.webSocketService = new WebSocketService(config.WEBSOCKETS_END_POINT);
}
